// QuestionView.tsx
import React, { useState, useEffect } from "react";
import {
  CheckCircle2,
  XCircle,
  ArrowRight,
  HelpCircle,
  AlertCircle,
} from "lucide-react";

export type Question = {
  id: number;
  question: string;
  options: string[];
  answer: string;
  explanation?: string;
};

interface QuestionViewProps {
  question: Question;
  currentQuestion: number;
  totalQuestions: number;
  selected: string | null;
  showAnswer: boolean;
  score: number;
  primaryColor?: string;
  onSelect: (option: string) => void;
  onNext: () => void;
  onViewQuestion?: (question: Question) => void; // Add this line
  isInDialog?: boolean;
}


export default function QuestionView({
  question,
  currentQuestion,
  totalQuestions,
  selected,
  showAnswer,
  score,
  primaryColor = "bg-emerald-600",
  onSelect,
  onNext,
  onViewQuestion, // Add this line
  isInDialog = false,
}: QuestionViewProps) {
  const [isExplanationExpanded, setIsExplanationExpanded] = useState(true);

  // Auto-scroll to explanation when shown
  useEffect(() => {
    if (showAnswer && question.explanation && isInDialog) {
      const explanationElement = document.getElementById('explanation-section');
      explanationElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [showAnswer, question.explanation, isInDialog]);

  const progress = ((currentQuestion + 1) / totalQuestions) * 100;
  const isLastQuestion = currentQuestion + 1 === totalQuestions;

  // Dialog-optimized version
  if (isInDialog) {
    return (
      <div className="w-full max-w-2xl mx-auto">
        {/* Compact Header for Dialog */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${primaryColor} text-white flex items-center justify-center font-bold text-sm`}>
                Q{currentQuestion + 1}
              </div>
              <div>
                <p className="text-xs text-gray-500">Question</p>
                <p className="font-semibold text-sm">{currentQuestion + 1}/{totalQuestions}</p>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-xs text-gray-500">Score</p>
              <p className="font-bold text-xl">{score}</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${primaryColor}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question Text */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold leading-relaxed break-words">
            {question.question}
          </h3>
        </div>

        {/* Options Grid */}
        <div className="space-y-2.5 mb-6">
          {question.options.map((option, index) => {
            const isCorrect = option === question.answer;
            const isSelected = option === selected;
            
            let optionClasses = "w-full rounded-xl border p-3 text-left transition-all duration-200 flex items-center justify-between gap-3";
            
            if (showAnswer) {
              if (isCorrect) {
                optionClasses += " bg-green-50 border-green-400 ring-1 ring-green-400";
              } else if (isSelected) {
                optionClasses += " bg-red-50 border-red-400 ring-1 ring-red-400";
              } else {
                optionClasses += " bg-gray-50 border-gray-200";
              }
            } else {
              optionClasses += " hover:border-gray-400 hover:shadow-sm bg-white cursor-pointer";
            }

            return (
              <button
                key={option}
                onClick={() => onSelect(option)}
                disabled={showAnswer}
                className={optionClasses}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                    !showAnswer ? 'bg-gray-100 text-gray-600' : 
                    isCorrect ? 'bg-green-500 text-white' :
                    isSelected ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {String.fromCharCode(65 + index)}
                  </span>
                  <span className="text-sm md:text-base font-medium break-words">
                    {option}
                  </span>
                </div>
                
                {showAnswer && isCorrect && <CheckCircle2 size={18} className="text-green-600 shrink-0" />}
                {showAnswer && isSelected && !isCorrect && <XCircle size={18} className="text-red-600 shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Explanation Section */}
        {showAnswer && question.explanation && (
          <div id="explanation-section" className="mb-6 rounded-xl bg-blue-50 border border-blue-200 overflow-hidden">
            <button
              onClick={() => setIsExplanationExpanded(!isExplanationExpanded)}
              className="w-full flex items-center justify-between p-3 hover:bg-blue-100/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <HelpCircle size={16} className="text-blue-600" />
                <h4 className="font-semibold text-blue-900 text-sm">Explanation</h4>
              </div>
              <span className="text-blue-600 text-xs">
                {isExplanationExpanded ? '▼' : '▶'}
              </span>
            </button>
            
            {isExplanationExpanded && (
              <div className="p-4 pt-0 border-t border-blue-200">
                <p className="text-blue-800 text-sm leading-relaxed">
                  {question.explanation}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Next Button */}
        {showAnswer && (
          <button
            onClick={onNext}
            className={`w-full ${primaryColor} h-12 rounded-xl text-white font-semibold flex items-center justify-center gap-2 shadow-lg hover:opacity-90 transition-all`}
          >
            {isLastQuestion ? (
              <>
                Finish Quiz
                <CheckCircle2 size={18} />
              </>
            ) : (
              <>
                Next Question
                <ArrowRight size={18} />
              </>
            )}
          </button>
        )}
      </div>
    );
  }

  // Original full-screen version (optimized)
  return (
    <div className="h-[calc(100vh-90px)] overflow-hidden flex items-center justify-center p-3 md:p-5">
      <div className="w-full max-w-5xl h-full bg-white rounded-2xl border shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="shrink-0 border-b bg-white/90 backdrop-blur-sm">
          <div className="p-4 md:p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Question</p>
                <h2 className="text-2xl md:text-3xl font-bold">
                  {currentQuestion + 1} / {totalQuestions}
                </h2>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500 mb-1">Score</p>
                <h2 className="text-2xl md:text-3xl font-bold">{score}</h2>
              </div>
            </div>
            
            <div className="mt-5 h-2 rounded-full bg-gray-100 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${primaryColor}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="p-4 md:p-7">
            {/* Question */}
            <div className="mb-6">
              <h1 className="text-xl md:text-2xl font-bold leading-relaxed break-words">
                {question.question}
              </h1>
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 gap-3 mb-6">
              {question.options.map((option, index) => {
                const isCorrect = option === question.answer;
                const isSelected = option === selected;
                
                let optionClasses = "relative w-full rounded-xl border p-4 text-left transition-all duration-200 flex items-center justify-between gap-4";
                
                if (showAnswer) {
                  if (isCorrect) {
                    optionClasses += " bg-green-50 border-green-500";
                  } else if (isSelected) {
                    optionClasses += " bg-red-50 border-red-500";
                  } else {
                    optionClasses += " bg-gray-50 border-gray-200";
                  }
                } else {
                  optionClasses += " hover:border-gray-400 hover:shadow-md bg-white cursor-pointer";
                }

                return (
                  <button
                    key={option}
                    onClick={() => onSelect(option)}
                    disabled={showAnswer}
                    className={optionClasses}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                        !showAnswer ? 'bg-gray-100 text-gray-700' : 
                        isCorrect ? 'bg-green-500 text-white' :
                        isSelected ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="text-base md:text-lg font-medium break-words">
                        {option}
                      </span>
                    </div>
                    
                    {showAnswer && isCorrect && <CheckCircle2 size={20} className="text-green-600 shrink-0" />}
                    {showAnswer && isSelected && !isCorrect && <XCircle size={20} className="text-red-600 shrink-0" />}
                  </button>
                );
              })}
            </div>

            {/* Explanation */}
            {showAnswer && question.explanation && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 mb-6">
                <div className="flex items-start gap-2">
                  <AlertCircle size={18} className="text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-1">Explanation</h3>
                    <p className="text-blue-800 text-sm leading-relaxed">
                      {question.explanation}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            {showAnswer && (
              <div className="flex justify-end">
                <button
                  onClick={onNext}
                  className={`${primaryColor} h-12 px-6 rounded-xl text-white font-semibold flex items-center gap-2 shadow-lg hover:opacity-90 transition-all`}
                >
                  {isLastQuestion ? "Finish Quiz" : "Next Question"}
                  <ArrowRight size={18} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Add custom scrollbar styles (add to your global CSS)
const scrollbarStyles = `
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
`;